import { useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";

const COLORS = ["#121214", "#6E7B5B", "#C07A5A", "#A8B3C2", "#D8C3A5"];

export default function AdminAnalytics() {
    const [overview, setOverview] = useState(null);
    const [orders, setOrders] = useState([]);
    useEffect(() => {
        api.get("/admin/overview").then(r => setOverview(r.data));
        api.get("/admin/orders").then(r => setOrders(r.data));
    }, []);

    if (!overview) return <p>Loading…</p>;

    // status distribution
    const statusDist = {};
    orders.forEach(o => { statusDist[o.status] = (statusDist[o.status] || 0) + 1; });
    const statusData = Object.entries(statusDist).map(([k, v]) => ({ name: k.replace("_", " "), value: v }));

    // category revenue
    const catRev = {};
    orders.filter(o => o.payment_status === "paid").forEach(o => {
        o.items.forEach(it => {
            const key = it.product_title.split(" ")[0];
            catRev[key] = (catRev[key] || 0) + it.subtotal;
        });
    });
    const catData = Object.entries(catRev).slice(0, 8).map(([k, v]) => ({ name: k, revenue: v }));

    return (
        <div data-testid="admin-analytics-page">
            <h1 className="font-display text-4xl">Analytics</h1>
            <p className="text-muted-foreground mt-1">Performance at a glance.</p>

            <div className="mt-6 grid lg:grid-cols-2 gap-6">
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6">
                        <h3 className="font-display text-2xl">Revenue — 14 days</h3>
                        <div className="h-64 mt-4">
                            <ResponsiveContainer>
                                <LineChart data={overview.revenue_chart}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E7DED2" />
                                    <XAxis dataKey="date" stroke="#857C72" fontSize={11} />
                                    <YAxis stroke="#857C72" fontSize={11} tickFormatter={v => `₹${(v/100).toLocaleString("en-IN")}`} />
                                    <Tooltip formatter={(v) => formatPrice(v)} contentStyle={{ borderRadius: 12 }} />
                                    <Line type="monotone" dataKey="revenue" stroke="#121214" strokeWidth={2.5} dot={{ r: 3 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6">
                        <h3 className="font-display text-2xl">Order statuses</h3>
                        <div className="h-64 mt-4">
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90}>
                                        {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                                    </Pie>
                                    <Tooltip contentStyle={{ borderRadius: 12 }} />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
                <Card className="lg:col-span-2 rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6">
                        <h3 className="font-display text-2xl">Top products (paid revenue)</h3>
                        <div className="h-72 mt-4">
                            <ResponsiveContainer>
                                <BarChart data={catData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#E7DED2" />
                                    <XAxis dataKey="name" stroke="#857C72" fontSize={11} />
                                    <YAxis stroke="#857C72" fontSize={11} tickFormatter={v => `₹${(v/100).toLocaleString("en-IN")}`} />
                                    <Tooltip formatter={(v) => formatPrice(v)} contentStyle={{ borderRadius: 12 }} />
                                    <Bar dataKey="revenue" fill="#121214" radius={[8, 8, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
